import React from 'react';
import PropTypes from 'prop-types';
import { LuTriangleAlert } from 'react-icons/lu';
import Badge from '../../../../components/ui/Badge';
import useInsuranceText from '../insuranceText';
import { INSURANCE_TYPES, daysLeft, endState, initialsOf, isBlank } from '../insuranceUtils';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';
const END_TONE = {
  expired: 'tw-font-semibold tw-text-rose-600 dark:tw-text-rose-400',
  soon: 'tw-font-semibold tw-text-amber-600 dark:tw-text-amber-400',
  ok: '',
  none: '',
};

const valueOf = (insurance, key) => {
  if (!key.includes('.')) return insurance[key];
  const [parent, child] = key.split('.');
  return insurance[parent]?.[child];
};

export const BesBadge = ({ value }) => {
  const { label } = useInsuranceText();
  return <Badge tone={value ? 'success' : 'neutral'}>{value ? label('bes.yes') : label('bes.no')}</Badge>;
};

BesBadge.propTypes = { value: PropTypes.bool };

/** "12 gün kaldı" / "Süresi doldu" for an end date (empty while it's far away). */
export const useEndLabel = () => {
  const { label } = useInsuranceText();
  return (endDate) => {
    const state = endState(endDate);
    if (state === 'expired') return label('state.expired');
    if (state === 'soon') {
      const days = daysLeft(endDate);
      return days === 0 ? label('state.today') : label('state.soon', { days });
    }
    return '';
  };
};

/** Tooltip of a cell: the full value and how long is left. */
export const useInsuranceCellTitle = () => {
  const { formatDate, formatMoney } = useInsuranceText();
  const endLabel = useEndLabel();
  return (insurance, key) => {
    const value = valueOf(insurance, key);
    if (key === 'hasBES') return undefined;
    if (key === 'cost') return formatMoney(value) || undefined;
    if (key.endsWith('.endDate')) return [formatDate(value), endLabel(value)].filter(Boolean).join(' · ') || undefined;
    if (key.endsWith('.startDate')) return formatDate(value) || undefined;
    return isBlank(value) ? undefined : String(value);
  };
};

/** Content of one insurance table cell; end dates show how soon they come. */
const InsuranceCell = ({ columnKey, insurance }) => {
  const { formatDate, formatMoney } = useInsuranceText();
  const value = valueOf(insurance, columnKey);

  if (columnKey === 'employeeName') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span aria-hidden="true" className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-rose-400/90 tw-to-pink-600/90 tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
          {initialsOf(insurance.employeeName)}
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{isBlank(value) ? '-' : String(value)}</span>
      </span>
    );
  }
  if (columnKey === 'hasBES') return <BesBadge value={Boolean(insurance.hasBES)} />;
  if (columnKey === 'cost') {
    const money = formatMoney(value);
    return money ? <span className="tw-font-semibold tw-tabular-nums">{money}</span> : <span className={MUTED}>-</span>;
  }
  const date = formatDate(value);
  if (!date) return <span className={MUTED}>-</span>;
  if (columnKey.endsWith('.endDate')) {
    const state = endState(value);
    return (
      <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums ${END_TONE[state]}`}>
        {state !== 'ok' && state !== 'none' && <LuTriangleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
        {date}
      </span>
    );
  }
  return <span className="tw-tabular-nums">{date}</span>;
};

InsuranceCell.propTypes = { columnKey: PropTypes.string.isRequired, insurance: PropTypes.object.isRequired };

/** One row of the card list (narrow screens). */
export const InsuranceCardRow = ({ insurance }) => {
  const { label, formatMoney } = useInsuranceText();
  const endLabel = useEndLabel();
  const warnings = INSURANCE_TYPES
    .map((type) => ({ type, text: endLabel(insurance[type]?.endDate), state: endState(insurance[type]?.endDate) }))
    .filter((item) => item.text);
  return (
    <>
      <span aria-hidden="true" className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-rose-400 tw-to-pink-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
        {initialsOf(insurance.employeeName)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{insurance.employeeName || '-'}</span>
          <BesBadge value={Boolean(insurance.hasBES)} />
        </span>
        {formatMoney(insurance.cost) && (
          <span className="tw-mt-0.5 tw-block tw-text-[13px] tw-font-semibold tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">{formatMoney(insurance.cost)}</span>
        )}
        {warnings.length > 0 && (
          <span className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-1">
            {warnings.map((item) => (
              <Badge key={item.type} tone={item.state === 'expired' ? 'danger' : 'warning'}>
                {label(`typesShort.${item.type}`)} · {item.text}
              </Badge>
            ))}
          </span>
        )}
      </span>
    </>
  );
};

InsuranceCardRow.propTypes = { insurance: PropTypes.object.isRequired };

export default InsuranceCell;
